import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./lib/supabase";
import type { Session } from "./lib/supabase";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import TablePage from "./pages/TablePage";
import WordsPage from "./pages/WordsPage";
import Concept1Page from "./pages/Concept1Page";
import Concept2Page from "./pages/Concept2Page";
import NuancePage from "./pages/NuancePage";
import UsersPage from "./pages/UsersPage";
import NoticesPage from "./pages/NoticesPage";
import TodosPage from "./pages/TodosPage";
import LoginLogsPage from "./pages/LoginLogsPage";
import StudyRecordsPage from "./pages/StudyRecordsPage";
import DBExplorerPage from "./pages/DBExplorerPage";
import ExpensesPage from "./pages/ExpensesPage";
import DesignAPage from "./pages/DesignAPage";
import "./App.css";

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  async function checkAdmin(s: Session | null) {
    if (!s?.user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("users")
      .select("id")
      .eq("id", s.user.id)
      .eq("is_admin", true)
      .maybeSingle();
    setIsAdmin(!!data);
    setLoading(false);
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      checkAdmin(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only re-check on actual sign-in/sign-out, not on token refresh
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        setLoading(true);
        setSession(session);
        checkAdmin(session);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <div className="loading-screen">Loading...</div>;

  return (
    <Routes>
      <Route path="/design" element={<DesignAPage />} />
      {session && isAdmin ? (
        <>
          <Route path="/" element={<LandingPage />} />
          <Route path="/v1" element={<DashboardLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="words" element={<WordsPage />} />
            <Route path="concept1" element={<Concept1Page />} />
            <Route path="concept2" element={<Concept2Page />} />
            <Route path="nuance" element={<NuancePage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="notices" element={<NoticesPage />} />
            <Route path="todos" element={<TodosPage />} />
            <Route path="login-logs" element={<LoginLogsPage />} />
            <Route path="study-records" element={<StudyRecordsPage />} />
            <Route path="db-explorer" element={<DBExplorerPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="table/:tableName" element={<TablePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <>
          <Route path="/login" element={<LoginPage isAdmin={isAdmin} session={session} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      )}
    </Routes>
  );
}
